import type { ColumnDef } from "@tanstack/react-table";
import { buildActionColumn, buildIconTextColumn, buildTagColumn } from "@/components/Table";
import { compareNullableIsoDates, renderEmptyCellState } from "@/components/Table/utils";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { sanitizeHtmlId } from "@/lib/htmlHelpers";
import { toFormat } from "@/lib/dateHelpers";

export type GroupStatus = "Active" | "Expired";

export type GroupTableRow = {
  id: string;
  groupName: string;
  entryReason: string | null;
  entryReasonZipcode: string | null;
  startDate: string;
  expiresAt: string | null;
  status: GroupStatus;
};

interface GetGroupHistoryColumnsOptions {
  canManageLeads: boolean;
  expiresAtSortDesc: boolean;
  onEditLead: () => void;
  setMenu: (menu: TableRowMenu) => void;
}

export function getGroupHistoryColumns({
  canManageLeads,
  expiresAtSortDesc,
  onEditLead,
  setMenu
}: GetGroupHistoryColumnsOptions): ColumnDef<GroupTableRow, unknown>[] {
  const columns: ColumnDef<GroupTableRow, unknown>[] = [
    {
      header: "Group Name",
      id: "groupName",
      accessorKey: "groupName",
      meta: { headerClassName: "hide-header-in-column", sortType: "text" }
    },
    buildIconTextColumn<GroupTableRow>({
      accessorKey: "entryReason",
      header: "Entry Reason",
      sortType: "text",
      getText: (row) => row.entryReason ?? "",
      iconPosition: "after",
      getIcon: (row) =>
        row.entryReasonZipcode
          ? {
              name: "info",
              size: 20,
              className: "text-sys-brand-on-surface-variant",
              tooltip: {
                supportingText: `Zip: ${row.entryReasonZipcode}`
              }
            }
          : null
    }),
    {
      header: "Start Date",
      id: "startDate",
      accessorKey: "startDate",
      meta: { sortType: "date" },
      cell: ({ row }) => renderEmptyCellState(toFormat(row.original.startDate))
    },
    {
      header: "Expires At",
      id: "expiresAt",
      accessorKey: "expiresAt",
      meta: { sortType: "date" },
      sortingFn: (rowA, rowB) =>
        compareNullableIsoDates(rowA.original.expiresAt, rowB.original.expiresAt, expiresAtSortDesc),
      cell: ({ row }) => renderEmptyCellState(row.original.expiresAt ? toFormat(row.original.expiresAt) : null)
    },
    buildTagColumn<GroupTableRow>({
      header: "Status",
      accessorKey: "status",
      getTagLabel: (row) => row.status,
      getTagType: (row) => (row.status === "Active" ? "success" : "surface"),
      sortType: "text"
    })
  ];

  if (canManageLeads) {
    columns.push(
      buildActionColumn<GroupTableRow>({
        getActions: (row) =>
          row.groupName === "Leads" ? [{ text: "Edit Lead", section: "Manage", onClick: onEditLead }] : [],
        actionId,
        setMenu,
        getRowId: (row) => sanitizeHtmlId(row.id)
      })
    );
  }

  return columns;
}
